import { Badge, Button, FormError, Input } from "@simplicity/ui";
import { X } from "lucide-react";
import { useState } from "react";

interface FieldsEditorProps {
  fields: string[];
  onChange: (fields: string[]) => void;
  label?: string;
  placeholder?: string;
}

export function FieldsEditor({
  fields,
  onChange,
  label = "Fields",
  placeholder = "Add field…",
}: FieldsEditorProps) {
  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState<string | null>(null);

  function addField(raw: string) {
    const value = raw.trim();
    if (!value) return;
    if (fields.some((f) => f.toLowerCase() === value.toLowerCase())) {
      setError(`"${value}" already exists`);
      return;
    }
    onChange([...fields, value]);
    setInputValue("");
    setError(null);
  }

  function removeField(index: number) {
    onChange(fields.filter((_, i) => i !== index));
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addField(inputValue);
    }
    // Backspace on empty input removes last field
    if (e.key === "Backspace" && inputValue === "" && fields.length > 0) {
      removeField(fields.length - 1);
    }
  }

  return (
    <div>
      <div className="form-label">{label}</div>
      <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-border bg-background p-2">
        {fields.map((field, i) => (
          <Badge key={`${field}-${i}`} variant="secondary" className="gap-1 pr-1">
            {field}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-4 w-4 p-0 hover:bg-destructive/20"
              onClick={() => removeField(i)}
            >
              <X className="h-3 w-3" />
            </Button>
          </Badge>
        ))}
        <Input
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setError(null);
          }}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            if (inputValue.trim()) addField(inputValue);
          }}
          placeholder={fields.length === 0 ? placeholder : ""}
          className="h-7 min-w-[120px] flex-1 border-none bg-transparent p-0 shadow-none focus-visible:ring-0"
        />
      </div>
      {error && <FormError>{error}</FormError>}
    </div>
  );
}
